/**
 * Circuit "insight" for the coach: is the kid one tap away from it working?
 * We try the circuit with every switch on / button pressed in a throwaway simulation.
 */
import { Board } from '../core/board';
import type { BuzzerState, GlowState, MotorState } from '../parts/defs';
import { Sim } from './sim';

function anythingWorking(board: Board, sim: Sim): boolean {
  for (const p of board.parts.values()) {
    const s = sim.states.get(p.id);
    if (!s) continue;
    if ((p.type === 'lamp' || p.type === 'led') && (s as GlowState).brightness > 0.3) return true;
    if (p.type === 'motor' && Math.abs((s as MotorState).omega) > 5) return true;
    if (p.type === 'buzzer' && (s as BuzzerState).sounding) return true;
  }
  return false;
}

/** Returns which control would make the circuit come alive, if any. */
export function readyToFlip(board: Board, live: Sim): 'switch' | 'switches' | 'button' | null {
  if (anythingWorking(board, live)) return null;
  const parts = [...board.parts.values()];
  const offSwitches = parts.filter((p) => p.type === 'switch' && !p.props.on);
  const buttons = parts.filter((p) => p.type === 'button');
  if (!offSwitches.length && !buttons.length) return null;
  const trial = Board.fromJSON(board.toJSON());
  for (const p of trial.parts.values()) if (p.type === 'switch') p.props.on = true;
  const sim = new Sim(trial);
  for (const b of buttons) sim.setInput(b.id, 'pressed', true);
  sim.run(45);
  if (!anythingWorking(trial, sim)) return null;
  return offSwitches.length > 1 ? 'switches' : offSwitches.length ? 'switch' : 'button';
}
